import React, { useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { Alert, Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, IconButton, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { DataLayout, FieldControl, StatusChip } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";

import { useAddArtefactReviewer, useDeleteArtefactReviewer, useFetchArtefactReviewers, useUpdateArtefactReviewer } from "../mutation";
import { useGetJeopardyStakeholders } from "../../../shared/hooks";
import { ArtefactDetails as ArtefactDetailsType, ArtefactReviewer } from "../types";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { useSession } from "../../auth";
import { useFetchApproval } from "../../stageGates/hooks/useFetchApprovals";
import { useGetSchedule } from "../../schedulePlanning";

const getInitials = (name: string) => name.split(" ").filter(Boolean).map((word) => word[0].toUpperCase()).join("").slice(0, 2);

const getReviewerErrorMessage = (error: unknown, fallback: string) => {
    if (isAxiosError(error)) {
        const serverMessage = error.response?.data?.message;
        if (typeof serverMessage === "string" && serverMessage) return serverMessage;
        if (error.response?.status === 403) return `${fallback} You may not have permission to do this.`;
    }
    return fallback;
};

export const ArtefactReviewersTab = ({ artefactId, artefactDetails, projectId }: {
    artefactId: string | null;
    artefactDetails: ArtefactDetailsType | undefined;
    projectId: string | undefined;
}) => {
    const [isAddingReviewer, setIsAddingReviewer] = useState(false);
    const [selectedReviewerId, setSelectedReviewerId] = useState<number | null>(null);
    const [reviewerActionError, setReviewerActionError] = useState<string>("");

    const { isAllowedCompleteArtefactReviewer, isAdminPerson } = useCapabilities();
    const { data: session } = useSession();
    const currentUserId = session?.user?.id ? Number(session.user.id) : null;

    const stageGateId = artefactDetails?.stage_gate_id ?? undefined;
    const { data: stageGateOwner } = useGetSchedule(stageGateId);
    const { data: stageGateApprovals } = useFetchApproval({ stageID: String(stageGateId || '') });

    const isStageGateOwner = !!stageGateOwner?.is_action_owner;
    const isStageGateApprover = (stageGateApprovals || []).some((approval: any) => Number(approval.user_id) === currentUserId);
    const isArtefactInReview = artefactDetails?.status === 'in_review';

    // Only admins add or delete reviewers.
    const canManageReviewers = isAdminPerson;
    const canManageReviewCompletion = isAllowedCompleteArtefactReviewer || isStageGateOwner || isStageGateApprover;
    const canManageReviewerDeletion = isAdminPerson;

    const canAddReviewer = canManageReviewers && isArtefactInReview;
    const canDeleteReviewer = canManageReviewerDeletion && isArtefactInReview;

    const { data: stakeholders, isLoading: isLoadingStakeholders } = useGetJeopardyStakeholders({ params: { project_id: projectId || artefactDetails?.project_id } });
    const stakeholderOptions = useMemo(() => (
        stakeholders?.data?.flatMap((role: any) =>
            role?.users?.map((user: any) => ({
                id: user.user_id,
                name: user.user_name,
                role: role.role_name,
            }))
        ) || []
    ), [stakeholders]);

    const { data: reviewersData, isLoading: isLoadingReviewers } = useFetchArtefactReviewers(artefactId);
    const reviewers: ArtefactReviewer[] = Array.isArray(reviewersData) ? reviewersData : [];
    const reviewedCount = reviewers.filter((reviewer) => reviewer.status !== 'awaiting_review').length;

    const addReviewer = useAddArtefactReviewer();

    const reviewerOptions = useMemo(() => {
        const existingIds = new Set(reviewers.map((reviewer: any) => reviewer.user_id));
        return stakeholderOptions
            .filter((user: any) => !existingIds.has(user.id))
            .map((user: any) => ({ value: user.id, label: user.name }));
    }, [stakeholderOptions, reviewers]);

    const handleCancelAddReviewer = () => {
        setReviewerActionError("");
        setIsAddingReviewer(false);
        setSelectedReviewerId(null);
    };

    const handleAddReviewer = async () => {
        if (!selectedReviewerId || !artefactId || !artefactDetails?.project_id) return;
        setReviewerActionError("");
        try {
            await addReviewer.mutateAsync({
                artefactId,
                payload: {
                    user_id: selectedReviewerId,
                    project_id: artefactDetails.project_id,
                    stage_gate_id: artefactDetails.stage_gate_id ?? null,
                    notes: null,
                },
            });
            handleCancelAddReviewer();
        } catch (error) {
            setReviewerActionError(getReviewerErrorMessage(error, "Unable to add the reviewer."));
        }
    };

    const updateReviewer = useUpdateArtefactReviewer();
    const [updatingReviewerId, setUpdatingReviewerId] = useState<number | null>(null);
    const [pendingReviewToggle, setPendingReviewToggle] = useState<Record<number, boolean>>({});

    const handleCancelReviewToggle = (reviewerId: number) => {
        setPendingReviewToggle((prev) => {
            const { [reviewerId]: _removed, ...rest } = prev;
            return rest;
        });
    };

    const handleConfirmReviewToggle = async (reviewer: ArtefactReviewer, nextChecked: boolean) => {
        if (!artefactId) return;
        const nextStatus = nextChecked ? 'reviewed' : 'awaiting_review';
        setReviewerActionError("");
        setUpdatingReviewerId(reviewer.id);
        try {
            await updateReviewer.mutateAsync({ artefactId, reviewerId: reviewer.id, payload: { status: nextStatus } });
            handleCancelReviewToggle(reviewer.id);
        } catch (error) {
            setReviewerActionError(getReviewerErrorMessage(error, "Unable to update the review status."));
        } finally {
            setUpdatingReviewerId(null);
        }
    };

    const deleteReviewer = useDeleteArtefactReviewer();
    const [deletingReviewerId, setDeletingReviewerId] = useState<number | null>(null);
    const [pendingDeleteIds, setPendingDeleteIds] = useState<Record<number, boolean>>({});

    const handleCancelDeleteReviewer = (reviewerId: number) => {
        setPendingDeleteIds((prev) => {
            const { [reviewerId]: _removed, ...rest } = prev;
            return rest;
        });
    };

    const handleConfirmDeleteReviewer = async (reviewerId: number) => {
        if (!artefactId) return;
        setReviewerActionError("");
        setDeletingReviewerId(reviewerId);
        try {
            await deleteReviewer.mutateAsync({ artefactId, reviewerId });
            handleCancelDeleteReviewer(reviewerId);
        } catch (error) {
            setReviewerActionError(getReviewerErrorMessage(error, "Unable to remove the reviewer."));
        } finally {
            setDeletingReviewerId(null);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
            {reviewerActionError && <Alert severity="error" onClose={() => setReviewerActionError("")}>{reviewerActionError}</Alert>}
            {
                !isAddingReviewer && canAddReviewer && (
                    <Button variant="outlined" onClick={() => setIsAddingReviewer(true)}>
                        Add Reviewer
                    </Button>
                )
            }
            {
                isAddingReviewer && canAddReviewer && (
                    <DataLayout title="Add Reviewer" bgVariant="filled">
                        <Box p={2} display={'flex'} flexDirection={'column'} gap={2}>
                            <FieldControl
                                keyName={"userId"}
                                label={"Select a Stakeholder"}
                                variant={"select"}
                                value={selectedReviewerId}
                                options={reviewerOptions}
                                onChange={(_key: string, value: any) => setSelectedReviewerId(value)}
                                disabled={isLoadingStakeholders}
                            />
                            <Divider />
                            <Box display={'flex'} justifyContent={'flex-end'} gap={2}>
                                <Button variant="text" onClick={handleCancelAddReviewer} disabled={addReviewer.isPending}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleAddReviewer}
                                    disabled={!selectedReviewerId || addReviewer.isPending}
                                    startIcon={addReviewer.isPending ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                                >
                                    Add Reviewer
                                </Button>
                            </Box>
                        </Box>
                    </DataLayout>
                )
            }
            <Box flex={1} sx={{ overflowY: 'auto' }}>
                <DataLayout
                    title="Reviewers"
                    bgVariant="filled"
                    action={<Typography variant="caption" color="text.secondary">{reviewedCount} of {reviewers.length} reviewed</Typography>}
                >
                    <Box p={2} display={'flex'} flexDirection={'column'} gap={2}>
                        {
                            !isArtefactInReview && reviewers.length > 0 && (
                                <Alert severity="info">Reviews can only be completed while the artefact is In Review.</Alert>
                            )
                        }
                        {
                            isLoadingReviewers ? (
                                <Box display={'flex'} justifyContent={'center'} py={2}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : reviewers.length === 0 ? (
                                <Alert severity="info">No reviewers assigned yet</Alert>
                            ) : (
                                reviewers.map((reviewer) => {
                                    const isReviewed = reviewer.status !== 'awaiting_review';
                                    const pendingChecked = pendingReviewToggle[reviewer.id];
                                    const hasPendingChange = pendingChecked !== undefined;
                                    const checkedValue = hasPendingChange ? pendingChecked : isReviewed;
                                    const canCompleteThisReview = isArtefactInReview && (canManageReviewCompletion || currentUserId === reviewer.user_id);
                                    const isPendingDelete = !!pendingDeleteIds[reviewer.id];
                                    const isDeletingThisReviewer = deletingReviewerId === reviewer.id;
                                    return (
                                        <Box
                                            key={reviewer.id}
                                            border="1px solid"
                                            borderColor="divider"
                                            borderRadius={1}
                                            p={2}
                                        >
                                            <Box display={'flex'} justifyContent={'space-between'} alignItems={'flex-start'}>
                                                <Box display={'flex'} alignItems={'center'} gap={1.5}>
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: "50%",
                                                            bgcolor: "divider",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            color: "text.primary",
                                                            fontSize: "0.8rem",
                                                            fontWeight: 600,
                                                            textTransform: "uppercase",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {getInitials(reviewer.user_name || 'N A')}
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" color="text.primary">
                                                            {reviewer.user_name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {reviewer.role_name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                {
                                                    !isReviewed && canDeleteReviewer && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setPendingDeleteIds((prev) => ({ ...prev, [reviewer.id]: true }))}
                                                            disabled={isDeletingThisReviewer}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    )
                                                }
                                            </Box>
                                            <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} mt={1}>
                                                <StatusChip
                                                    label={isReviewed ? `Reviewed ${formatDate(reviewer.reviewed_date || '')}` : reviewer.status_display_text || 'Awaiting Review'}
                                                    color={isReviewed ? 'green' : 'grey'}
                                                    variant="filled"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={checkedValue}
                                                            onChange={(event) => setPendingReviewToggle((prev) => ({ ...prev, [reviewer.id]: event.target.checked }))}
                                                            disabled={isReviewed || updatingReviewerId === reviewer.id || !canCompleteThisReview || isPendingDelete || isDeletingThisReviewer}
                                                        />
                                                    }
                                                    label="Reviewed"
                                                />
                                            </Box>
                                            {
                                                isPendingDelete && (
                                                    <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} gap={1} mt={1}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Remove this reviewer?
                                                        </Typography>
                                                        <Box display={'flex'} gap={1}>
                                                            <Button
                                                                variant="text"
                                                                size="small"
                                                                onClick={() => handleCancelDeleteReviewer(reviewer.id)}
                                                                disabled={isDeletingThisReviewer}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                variant="contained"
                                                                color="error"
                                                                size="small"
                                                                onClick={() => handleConfirmDeleteReviewer(reviewer.id)}
                                                                disabled={isDeletingThisReviewer}
                                                                loading={isDeletingThisReviewer}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                )
                                            }
                                            {
                                                hasPendingChange && (
                                                    <Box display={'flex'} justifyContent={'flex-end'} gap={1} mt={1}>
                                                        <Button
                                                            variant="text"
                                                            size="small"
                                                            onClick={() => handleCancelReviewToggle(reviewer.id)}
                                                            disabled={updatingReviewerId === reviewer.id}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={() => handleConfirmReviewToggle(reviewer, pendingChecked)}
                                                            disabled={updatingReviewerId === reviewer.id}
                                                            loading={updatingReviewerId === reviewer.id}
                                                        >
                                                            Confirm
                                                        </Button>
                                                    </Box>
                                                )
                                            }
                                        </Box>
                                    );
                                })
                            )
                        }
                    </Box>
                </DataLayout>
            </Box>
        </Box>
    );
};
